import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Establishment } from '../../../../../../graphql/generated';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '../../../../shared/components/input/group/input-group.component';
import { SectionComponent } from '../../../../shared/components/input/section/section.component';
import { SelectComponent } from '../../../../shared/components/select/select.component';
import { IOption } from '../../../../shared/components/select/typings/option';
import { AppService } from '../../../services/app.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

export type EstablishmentFormValues = ReturnType<EstablishmentFormComponent['createForm']>['value'];

@Component({
    selector: 'app-establishment-form',
    templateUrl: './establishment-form.component.html',
    styleUrl: './establishment-form.component.scss',
    imports: [ReactiveFormsModule, SectionComponent, InputGroupComponent, SelectComponent],
})
export class EstablishmentFormComponent implements OnInit {

    private readonly appService = inject(AppService);

    establishmentForm = this.createForm();

    // Parent establishment options (excluding current establishment if editing)
    parentEstablishmentOptions = toSignal(
        this.appService.accessibleEstablishments$.pipe(
            map(establishments => {
                const options: IOption[] = [{ value: null, label: 'Geen (hoofdvestiging)' }];
                establishments
                    .filter(e => !this.establishment || e.id !== this.establishment.id)
                    .forEach(e => options.push({ value: e.id, label: e.name }));
                return options;
            }),
        ),
        { initialValue: [{ value: null, label: 'Geen (hoofdvestiging)' }] },
    );

    // ----------[ Input ]----------

    @Input()
    establishment: Establishment;

    // ----------[ Output ]----------

    @Output()
    public readonly cancelled = new EventEmitter<void>();

    @Output()
    public readonly submitted = new EventEmitter<EstablishmentFormValues>();

    constructor(private readonly formBuilder: FormBuilder) {
    }

    // ----------[ Lifecycle Hooks ]----------

    ngOnInit() {
        if (this.establishment) {
            this.setDefaultValues();
        }
    }

    // ----------[ Public Methods ]----------

    submit() {
        this.submitted.emit(this.establishmentForm.value);
    }

    // ----------[ Private Methods ]----------

    private createForm() {
        return this.formBuilder.group({
            name: ['', Validators.required],
            commissionPercentage: [100, [Validators.required, Validators.min(0), Validators.max(100)]],
            parentEstablishmentId: [null as string | null],
            // Billing info
            billingCompanyName: [''],
            billingAddress: [''],
            billingPostalCode: [''],
            billingCity: [''],
            billingCountry: ['Nederland'],
            billingKvkNumber: [''],
            billingVatNumber: [''],
            billingIban: [''],
            billingPhone: [''],
            billingEmail: ['', Validators.email],
        });
    }

    private setDefaultValues() {
        const billingInfo = this.establishment.billingInfo;
        
        this.establishmentForm.setValue({
            name: this.establishment.name || '',
            commissionPercentage: this.establishment.commissionPercentage || 100,
            parentEstablishmentId: this.establishment.parentEstablishmentId || null,
            // Billing info
            billingCompanyName: billingInfo?.companyName || '',
            billingAddress: billingInfo?.address || '',
            billingPostalCode: billingInfo?.postalCode || '',
            billingCity: billingInfo?.city || '',
            billingCountry: billingInfo?.country || 'Nederland',
            billingKvkNumber: billingInfo?.kvkNumber || '',
            billingVatNumber: billingInfo?.vatNumber || '',
            billingIban: billingInfo?.iban || '',
            billingPhone: billingInfo?.phone || '',
            billingEmail: billingInfo?.email || '',
        });
    }

}
